import { useEffect, useRef, type ReactNode } from 'react';

import { CloseButton, Overlay } from './Overlay';

/**
 * The dialog frame every "Add" and "Edit" in the admin uses.
 *
 * v2 moves creating and editing a feature, a plan, a version, a question and a
 * review out of the row and into a dialog. The rows become a list you read; the
 * dialog is where you write. Written once here because the design draws the
 * same frame six times: a title with the record it belongs to beside it, the
 * fields, and a footer on `--panel2` with Cancel and one accent action.
 */
export function Modal({
  title,
  context,
  submitLabel,
  submitting,
  canSubmit = true,
  error,
  onSubmit,
  onClose,
  children,
  width = 440,
}: {
  title: string;
  /** Which record this is about, in the design's small mono. */
  context?: string;
  submitLabel: string;
  submitting?: boolean;
  canSubmit?: boolean;
  error?: string | null;
  onSubmit: () => void;
  onClose: () => void;
  children: ReactNode;
  width?: number;
}) {
  const form = useRef<HTMLFormElement>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    // The first field, so somebody can start typing without reaching for the
    // mouse — the dialog opened because they already decided to write.
    form.current?.querySelector<HTMLElement>('input, textarea, select')?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <Overlay onBackdropClick={onClose}>
      <form
        ref={form}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit();
        }}
        className="m-auto flex max-h-[92vh] w-full flex-col overflow-hidden rounded-[14px] border"
        style={{
          maxWidth: width,
          background: 'var(--panel)',
          borderColor: 'var(--line2)',
          boxShadow: '0 30px 80px rgba(0, 0, 0, 0.6)',
        }}
      >
        <div
          className="flex flex-none items-center gap-2.5 border-b px-4 py-3"
          style={{ borderColor: 'var(--line)' }}
        >
          <span className="text-sm font-semibold">{title}</span>
          {context ? (
            <span className="truncate font-mono text-[9.5px]" style={{ color: 'var(--dim)' }}>
              {context}
            </span>
          ) : null}
          <CloseButton onClick={onClose} />
        </div>

        <div className="flex flex-col gap-3 overflow-auto px-4 py-3.5">
          {children}
          {error ? (
            <span
              role="alert"
              className="text-[11.5px] leading-[1.5]"
              style={{ color: 'var(--danger)' }}
            >
              {error}
            </span>
          ) : null}
        </div>

        <div
          className="flex flex-none justify-end gap-2 border-t px-4 py-3"
          style={{ borderColor: 'var(--line)', background: 'var(--panel2)' }}
        >
          <button
            type="button"
            onClick={onClose}
            className="btn-outline focus-ring min-h-8 cursor-pointer px-3 py-[7px] text-[12.5px]"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSubmit || submitting}
            className="btn-accent focus-ring min-h-8 cursor-pointer px-[13px] py-[7px] text-[12.5px] disabled:cursor-default disabled:opacity-50"
          >
            {submitting ? 'Saving…' : submitLabel}
          </button>
        </div>
      </form>
    </Overlay>
  );
}

/** A labelled field inside a dialog, in the design's proportions. */
export function ModalField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-[5px]">
      <span className="field-label">{label}</span>
      {children}
    </label>
  );
}
